/**
 * PDF Processing Service - Extract text từ PDF files
 * Sử dụng pdf.js để parse PDF và extract nội dung
 */

import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy, TextItem } from 'pdfjs-dist/types/src/display/api'

// Configure worker path
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
}

export interface PDFProcessingResult {
  success: boolean
  text?: string
  pageCount?: number
  metadata?: {
    title?: string
    author?: string
    subject?: string
    keywords?: string
    creationDate?: string
  }
  error?: string
}

export interface PDFExtractionOptions {
  maxPages?: number // Giới hạn số trang (để tránh files quá lớn)
  removeExtraWhitespace?: boolean // Loại bỏ khoảng trắng thừa
  normalizeText?: boolean // Chuẩn hóa text
}

class PDFProcessingService {
  /**
   * Extract text từ PDF file
   */
  async extractTextFromPDF(
    file: File,
    options: PDFExtractionOptions = {}
  ): Promise<PDFProcessingResult> {
    const {
      maxPages = 50, // Giới hạn 50 trang mặc định
      removeExtraWhitespace = true,
      normalizeText = true
    } = options

    try {
      // Validate file type
      if (!file.type.includes('pdf')) {
        return {
          success: false,
          error: 'File không phải là PDF. Vui lòng chọn file PDF.'
        }
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'File PDF quá lớn (tối đa 10MB). Vui lòng chọn file nhỏ hơn.'
        }
      }

      console.log('🔄 Đang xử lý PDF:', file.name, `(${(file.size / 1024).toFixed(2)} KB)`)

      // Load PDF
      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf: PDFDocumentProxy = await loadingTask.promise

      console.log('📄 PDF loaded:', pdf.numPages, 'pages')

      // Get metadata
      const metadata = await this.extractMetadata(pdf)

      // Check page count
      const pagesToProcess = Math.min(pdf.numPages, maxPages)
      if (pdf.numPages > maxPages) {
        console.warn(`⚠️ PDF có ${pdf.numPages} trang, chỉ xử lý ${maxPages} trang đầu tiên`)
      }

      // Extract text from all pages
      let fullText = ''
      for (let pageNum = 1; pageNum <= pagesToProcess; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const textContent = await page.getTextContent()
        
        // Combine text items
        const pageText = textContent.items
          .map((item) => {
            if ('str' in item) {
              return (item as TextItem).str
            }
            return ''
          })
          .join(' ')
        
        fullText += pageText + '\n\n'
        
        console.log(`📖 Đã xử lý trang ${pageNum}/${pagesToProcess}`)
      }

      // Post-process text
      let processedText = fullText

      if (removeExtraWhitespace) {
        processedText = this.removeExtraWhitespace(processedText)
      }

      if (normalizeText) {
        processedText = this.normalizeText(processedText)
      }

      console.log('✅ Hoàn thành extract text:', processedText.length, 'ký tự')

      // Validate text content
      if (!processedText || processedText.trim().length < 50) {
        console.warn('⚠️ PDF không chứa đủ text hoặc là PDF scan')
        return {
          success: false,
          error: 'PDF không chứa text hoặc là PDF scan (image). Vui lòng sử dụng PDF text-based có thể copy được text.',
          pageCount: pdf.numPages,
          metadata
        }
      }

      return {
        success: true,
        text: processedText,
        pageCount: pdf.numPages,
        metadata
      }
    } catch (error) {
      console.error('❌ Lỗi khi xử lý PDF:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Có lỗi xảy ra khi xử lý PDF'
      }
    }
  }

  /**
   * Extract metadata từ PDF
   */
  private async extractMetadata(pdf: PDFDocumentProxy): Promise<PDFProcessingResult['metadata']> {
    try {
      const metadata = await pdf.getMetadata()
      const info = metadata.info as any

      return {
        title: info?.Title || undefined,
        author: info?.Author || undefined,
        subject: info?.Subject || undefined,
        keywords: info?.Keywords || undefined,
        creationDate: info?.CreationDate || undefined
      }
    } catch (error) {
      console.warn('⚠️ Không thể lấy metadata:', error)
      return {}
    }
  }

  /**
   * Loại bỏ khoảng trắng thừa
   */
  private removeExtraWhitespace(text: string): string {
    return text
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Remove excessive line breaks
      .trim()
  }

  /**
   * Chuẩn hóa text
   */
  private normalizeText(text: string): string {
    return text
      .replace(/['']/g, "'") // Normalize quotes
      .replace(/[""]/g, '"') // Normalize double quotes
      .replace(/–/g, '-') // Normalize dashes
      .replace(/…/g, '...') // Normalize ellipsis
      .trim()
  }

  /**
   * Extract keywords từ text
   * Simple keyword extraction - lấy từ có tần suất cao
   */
  extractKeywords(text: string, maxKeywords: number = 10): string[] {
    // Danh sách stopwords tiếng Việt đơn giản
    const stopwords = new Set([
      'là', 'của', 'và', 'có', 'được', 'trong', 'với', 'cho', 'một', 'các',
      'này', 'đó', 'những', 'từ', 'về', 'để', 'khi', 'như', 'thì', 'sẽ',
      'đã', 'đang', 'hay', 'hoặc', 'nhưng', 'mà', 'nếu', 'do', 'vì', 'nên'
    ])

    // Tách từ và lọc
    const words = text
      .toLowerCase()
      .replace(/[^\w\sàáảãạăắằẳẵặâấầẩẫậèéẻẽẹêếềểễệìíỉĩịòóỏõọôốồổỗộơớờởỡợùúủũụưứừửữựỳýỷỹỵđ]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopwords.has(word))

    // Đếm tần suất
    const wordCount = new Map<string, number>()
    words.forEach(word => {
      wordCount.set(word, (wordCount.get(word) || 0) + 1)
    })

    // Sắp xếp theo tần suất và lấy top keywords
    const keywords = Array.from(wordCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxKeywords)
      .map(([word]) => word)

    return keywords
  }

  /**
   * Chunk text thành các đoạn nhỏ hơn
   * Hữu ích cho việc gửi đến AI (token limit)
   */
  chunkText(text: string, maxChunkSize: number = 2000): string[] {
    const chunks: string[] = []
    const paragraphs = text.split('\n\n')
    
    let currentChunk = ''
    
    for (const paragraph of paragraphs) {
      if (currentChunk.length + paragraph.length > maxChunkSize) {
        if (currentChunk) {
          chunks.push(currentChunk.trim())
        }
        currentChunk = paragraph
      } else {
        currentChunk += (currentChunk ? '\n\n' : '') + paragraph
      }
    }
    
    if (currentChunk) {
      chunks.push(currentChunk.trim())
    }
    
    return chunks
  }
}

// Export singleton instance
export const pdfProcessingService = new PDFProcessingService()
export default pdfProcessingService

